import { useMemo, useState } from "react";
import classNames from "classnames";
import Banner from "@/components/Banner";
import CopyButton from "@/components/CopyButton";
import DetailsCard from "@/components/DetailsCard";
import EmptyState from "@/components/EmptyState";
import ListItem from "@/components/ListItem";
import { ListItemSkeleton } from "@/components/Skeleton";
import SubSectionHeading from "@/components/SubSectionHeading";
import Tag from "@/components/Tag";
import { toFormat, toLocaleDate } from "@/lib/dateHelpers";
import type { AgentChatConversation, AgentChatTurn } from "./queries";

export type AgentChatLogsViewerProps = {
  conversations: AgentChatConversation[];
  loading?: boolean;
  error?: boolean;
};

function toolNames(invocations: unknown): string[] {
  if (!Array.isArray(invocations)) return [];

  return invocations.flatMap((item) => {
    if (item && typeof item === "object" && "name" in item && typeof (item as { name: unknown }).name === "string") {
      return [(item as { name: string }).name];
    }
    return [];
  });
}

const MIN_REDUNDANT_PREVIEW_CHARS = 80;

function normalizeLogText(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function stripPreviewTruncation(value: string): string {
  return value.replace(/(?:\s*…\(truncated\)|\s*\.\.\.\(truncated\))\s*$/u, "").trim();
}

function toolResultsAreRedundant(
  assistantReply: string | null | undefined,
  toolResultsPreview: string | null | undefined
): boolean {
  const preview = stripPreviewTruncation(normalizeLogText(toolResultsPreview));
  if (!preview) return true;

  const assistant = normalizeLogText(assistantReply);
  if (!assistant) return false;
  if (assistant === preview) return true;

  // Logger truncates only tool_results_preview. A short assistant lead that is a
  // prefix of a longer preview is distinct tool text and must stay visible.
  return preview.length >= MIN_REDUNDANT_PREVIEW_CHARS && assistant.startsWith(preview);
}

function loggedAtMs(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function conversationsNewestFirst(conversations: AgentChatConversation[]): AgentChatConversation[] {
  return [...conversations].sort((left, right) => loggedAtMs(right.lastLoggedAt) - loggedAtMs(left.lastLoggedAt));
}

function turnsNewestFirst(turns: AgentChatTurn[]): AgentChatTurn[] {
  return [...turns].sort((left, right) => loggedAtMs(right.loggedAt) - loggedAtMs(left.loggedAt));
}

function groupConversationsByDay(conversations: AgentChatConversation[]) {
  const groups: { day: string; conversations: AgentChatConversation[] }[] = [];
  const indexByDay = new Map<string, number>();

  conversationsNewestFirst(conversations).forEach((conversation) => {
    const day = toLocaleDate(conversation.lastLoggedAt) || "Unknown date";
    const existingIndex = indexByDay.get(day);
    if (existingIndex == null) {
      indexByDay.set(day, groups.length);
      groups.push({ day, conversations: [conversation] });
      return;
    }
    groups[existingIndex].conversations.push(conversation);
  });

  return groups;
}

function ConversationTurn({ turn, turnNumber }: { turn: AgentChatTurn; turnNumber: number }) {
  const tools = toolNames(turn.toolInvocations);
  const loggedAt = toFormat(turn.loggedAt, "h:mm a") || turn.loggedAt;
  const flags = turn.flags ?? [];

  return (
    <DetailsCard
      heading={`Turn ${turnNumber}`}
      caption={`${loggedAt}${turn.durationMs ? ` · ${turn.durationMs} ms` : ""}`}
      options={[
        {
          overline: "Staff",
          headline: turn.userMessage || "(empty)",
          longContent: true
        },
        {
          overline: "Assistant",
          headline: turn.assistantReply || "(empty)",
          longContent: true
        },
        ...(tools.length > 0
          ? [
              {
                overline: "Tools",
                headline: tools.join(", ")
              }
            ]
          : []),
        ...(turn.toolResultsPreview && !toolResultsAreRedundant(turn.assistantReply, turn.toolResultsPreview)
          ? [
              {
                overline: "Tool results",
                headline: turn.toolResultsPreview,
                longContent: true
              }
            ]
          : []),
        ...(turn.error
          ? [
              {
                overline: "Error",
                headline: turn.error,
                longContent: true
              }
            ]
          : [])
      ]}
    >
      {flags.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {flags.map((flag) => (
            <Tag key={flag} size="small" tagType={flag === "error" ? "error" : "warn"} tagStyle="standard">
              {flag}
            </Tag>
          ))}
        </div>
      ) : null}
      {turn.improvementPrompt ? (
        <DetailsCard
          heading="Improvement Prompt"
          compact
          actionButtons={
            <CopyButton
              textToCopy={turn.improvementPrompt}
              copyFieldLabel="Improvement prompt"
              feedback="snackbar"
              testId={`copy-improvement-prompt-${turn.id}`}
            >
              Copy prompt
            </CopyButton>
          }
        >
          <p className="whitespace-pre-wrap text-body-medium text-sys-brand-on-surface">{turn.improvementPrompt}</p>
        </DetailsCard>
      ) : null}
    </DetailsCard>
  );
}

export default function AgentChatLogsViewer({
  conversations,
  loading = false,
  error = false
}: AgentChatLogsViewerProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const orderedConversations = useMemo(() => conversationsNewestFirst(conversations), [conversations]);
  const selected =
    orderedConversations.find((conversation) => conversation.id === selectedId) ?? orderedConversations[0] ?? null;
  const selectedTurns = useMemo(() => (selected ? turnsNewestFirst(selected.turns) : []), [selected]);
  const groups = useMemo(() => groupConversationsByDay(conversations), [conversations]);

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <Banner
          variant="error"
          description="We couldn't load chat logs."
          subText="Try again in a moment."
          hideSecondaryDismiss
        />
        <EmptyState
          iconName="error"
          title="Couldn't Load Chat Logs"
          caption="The anonymized conversation list is unavailable right now."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 gap-6" data-testid="agent-chat-logs-loading">
        <div className="w-80 shrink-0">
          <ListItemSkeleton rows={6} />
        </div>
        <div className="min-w-0 flex-1">
          <ListItemSkeleton rows={4} />
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <EmptyState
        iconName="forum"
        title="No Chat Logs Yet"
        caption="When you use Chat with Member 360, anonymized conversations show up here."
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6" data-testid="agent-chat-logs-viewer">
      <aside className="min-h-0 w-full shrink-0 overflow-y-auto lg:w-80" data-testid="agent-chat-logs-list">
        {groups.map((group) => (
          <div key={group.day} className="mb-6 last:mb-0">
            <SubSectionHeading className="px-2 pb-2">{group.day}</SubSectionHeading>
            {group.conversations.map((conversation) => {
              const selectedConversation = conversation.id === selected?.id;
              return (
                <div
                  key={conversation.id}
                  data-testid={`agent-chat-log-${conversation.id}`}
                  aria-current={selectedConversation ? "true" : undefined}
                >
                  <ListItem
                    compact
                    className={classNames(selectedConversation && "rounded-lg bg-sys-brand-surface-container")}
                    overline={toFormat(conversation.lastLoggedAt, "h:mm a") || conversation.lastLoggedAt}
                    headline={conversation.preview || "Untitled conversation"}
                    supportingText={(conversation.qualityFlags ?? []).join(", ") || undefined}
                    tagText={conversation.needsImprovement ? "Needs Work" : undefined}
                    tagType="warn"
                    tagStyle="standard"
                    longContent
                    action={() => setSelectedId(conversation.id)}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </aside>
      <section className="min-h-0 min-w-0 flex-1 overflow-y-auto" data-testid="agent-chat-logs-detail">
        {selected ? (
          <div className="flex flex-col gap-6">
            {selectedTurns.map((turn, index) => (
              <ConversationTurn key={turn.id} turn={turn} turnNumber={selectedTurns.length - index} />
            ))}
          </div>
        ) : (
          <EmptyState
            iconName="chat"
            title="Select a Conversation"
            caption="Pick a conversation from the list to read the anonymized turns."
          />
        )}
      </section>
    </div>
  );
}
